/**
 * Payment Gateway Component
 * 
 * A reusable payment component that abstracts the payment gateway implementation.
 * Currently uses Razorpay, but can be easily switched to another provider.
 * 
 * Props:
 * - amount: Payment amount in rupees
 * - currency: Currency code (default: INR)
 * - customerInfo: Customer details (name, email, phone)
 * - orderDetails: Order/booking details
 * - onSuccess: Callback when payment succeeds
 * - onFailure: Callback when payment fails
 * - onCancel: Callback when payment is cancelled
 */

import React, { useState } from 'react';
import { CreditCard, Wallet, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { razorpayService } from '../../services/razorpayService';

const PaymentGateway = ({
  amount,
  currency = 'INR',
  customerInfo = {},
  orderDetails = {},
  onSuccess,
  onFailure,
  onCancel,
  disabled = false
}) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', null
  const [error, setError] = useState(null);

  /**
   * Handle payment initiation
   */
  const handlePayment = async (method) => {
    if (disabled || isProcessing) return;

    setIsProcessing(true);
    setError(null);
    setPaymentStatus(null);
    setPaymentMethod(method);

    try {
      console.log('🚀 Initiating payment:', { method, amount, customerInfo });

      // Step 1: Create order via backend
      const order = await razorpayService.createOrder({
        amount: amount,
        currency: currency,
        receipt: `booking_${Date.now()}`,
        notes: {
          ...orderDetails,
          payment_method: method
        }
      });

      // Step 2: Open Razorpay checkout
      const paymentResponse = await razorpayService.processPayment({
        orderId: order.id,
        amount: amount,
        currency: currency,
        name: 'Ayur Parishkar Kendra',
        description: orderDetails.description || 'Appointment Booking',
        prefill: {
          name: customerInfo.name || '',
          email: customerInfo.email || '',
          contact: customerInfo.phone || ''
        }
      });

      // Step 3: Verify payment on backend
      const verification = await razorpayService.verifyPayment({
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_signature: paymentResponse.razorpay_signature
      });

      if (verification.verified) {
        console.log('✅ Payment verified successfully');
        setPaymentStatus('success');
        
        // Call success callback with payment details
        if (onSuccess) {
          onSuccess({
            paymentId: paymentResponse.razorpay_payment_id,
            orderId: paymentResponse.razorpay_order_id,
            amount: amount,
            currency: currency,
            method: method,
            status: 'paid',
            verificationData: verification
          });
        }
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      setPaymentStatus('failed');
      setError(error.message || 'Payment failed. Please try again.');
      
      // Determine if it was cancelled or failed
      if (error.message.includes('cancelled')) {
        if (onCancel) onCancel(error);
      } else {
        if (onFailure) onFailure(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Payment Method Selection */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
          Select Payment Method
        </h3>

        {/* UPI Payment */}
        <button
          onClick={() => handlePayment('upi')}
          disabled={disabled || isProcessing}
          className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
            paymentMethod === 'upi' && isProcessing
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
          } ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="flex items-center">
            <Wallet className="w-6 h-6 mr-3 text-purple-600" />
            <div className="text-left">
              <p className="font-semibold text-gray-800">UPI Payment</p>
              <p className="text-sm text-gray-600">Pay with Google Pay, PhonePe, Paytm, etc.</p>
            </div>
          </div>
          {paymentMethod === 'upi' && isProcessing && (
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          )}
        </button>

        {/* Card Payment */}
        <button
          onClick={() => handlePayment('card')}
          disabled={disabled || isProcessing}
          className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
            paymentMethod === 'card' && isProcessing
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
          } ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="flex items-center">
            <CreditCard className="w-6 h-6 mr-3 text-purple-600" />
            <div className="text-left">
              <p className="font-semibold text-gray-800">Credit/Debit Card</p>
              <p className="text-sm text-gray-600">Secure payment with your card</p>
            </div>
          </div>
          {paymentMethod === 'card' && isProcessing && (
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          )}
        </button>
      </div>

      {/* Payment Status Messages */}
      {paymentStatus === 'success' && (
        <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <p className="text-green-800 font-medium">Payment successful!</p>
        </div>
      )}

      {paymentStatus === 'failed' && error && (
        <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Payment Failed</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
          <p className="text-blue-800">Processing payment...</p>
        </div>
      )}
    </div>
  );
};

export default PaymentGateway;

